/**
 * playSceneCheckpoint.ts
 * Checkpoint snapshot/restore helpers extracted from `PlayScene.ts` to keep
 * that scene under the AGENTS.md 300-line cap. Pure data-mutation helpers —
 * no React, no Skia, no async.
 *
 * @module playSceneCheckpoint
 */

import { gameState } from '../engine/gameState';
import type { ProjectileKind } from '../entities/Projectile';
import { resetProjectiles } from '../engine/projectileSystem';
import { clearParticles } from '../engine/particleSystem';
import { resetHitEffectVfx } from '../engine/hitEffectVfx';
import Logger from '../utils/logger';

/**
 * Snapshot of the player's collectible/score state at a checkpoint, used to
 * roll the level back to a known-good state when Nova dies.
 */
export interface CheckpointSnapshot {
  /** Set of collectible IDs that were collected before the snapshot. */
  collectibleIds: Set<string>;
  seeds: number;
  shardsCollected: number;
  score: number;
  heartFragments: number;
  maxHealth: number;
  /** Active weapon kind at snapshot time (typically `'star_shard'`). */
  projectileKind: ProjectileKind;
  /** Ammo count for the active non-default weapon at snapshot time. */
  ammo: number;
  /** Set of weapon-pickup IDs that were collected before the snapshot. */
  weaponPickupIds: Set<string>;
  /** Identifier of the checkpoint this snapshot was taken at (`null` = level start). */
  checkpointId: string | null;
}

/**
 * Build a snapshot from the current `gameState`. Called when a new checkpoint
 * is activated and at level start.
 *
 * @param checkpointId - The active checkpoint ID, or `null` for level start.
 * @returns A fresh snapshot capturing collectible + scoring state.
 */
export function takeCheckpointSnapshot(checkpointId: string | null): CheckpointSnapshot {
  const collectibleIds = new Set<string>();
  for (const c of gameState.collectibles) {
    if (c.collected) collectibleIds.add(c.id);
  }
  // DECISION: Mirror the collectibleIds pattern for weapon pickups — capture
  // the id-set at snapshot time so restore is symmetric with collectibles
  // (re-spawn anything collected after the checkpoint, leave pre-checkpoint
  // pickups consumed).
  const weaponPickupIds = new Set<string>();
  for (const p of gameState.weaponPickups) {
    if (p.collected) weaponPickupIds.add(p.id);
  }
  Logger.debug('PlayScene', `Checkpoint snapshot taken (cp: ${checkpointId ?? 'start'})`);
  return {
    collectibleIds,
    seeds: gameState.seeds,
    shardsCollected: gameState.shardsCollected,
    score: gameState.score,
    heartFragments: gameState.heartFragments,
    maxHealth: gameState.nova.maxHealth,
    projectileKind: gameState.projectileKind,
    ammo: gameState.ammo,
    weaponPickupIds,
    checkpointId,
  };
}

/**
 * Restore `gameState` to the given snapshot. Resets enemies/projectiles/
 * particles so the next life starts from a clean transient slate.
 *
 * DECISION: Health is set to the snapshotted `maxHealth` (not `NOVA_MAX_HP`)
 * so re-collected Heart Fragments don't grant duplicate bonuses, and the
 * player respawns at full HP relative to their current max.
 *
 * @param snapshot - The snapshot produced by `takeCheckpointSnapshot`.
 */
export function restoreCheckpointSnapshot(snapshot: CheckpointSnapshot): void {
  for (const c of gameState.collectibles) {
    c.collected = snapshot.collectibleIds.has(c.id);
  }
  // DECISION: Roll weapon pickup `.collected` flags back symmetrically with
  // collectibles — any pickup consumed after the snapshot becomes available
  // again so a player who picked up a weapon and then died can re-grab it.
  for (const p of gameState.weaponPickups) {
    p.collected = snapshot.weaponPickupIds.has(p.id);
  }
  gameState.seeds = snapshot.seeds;
  gameState.shardsCollected = snapshot.shardsCollected;
  gameState.score = snapshot.score;
  gameState.heartFragments = snapshot.heartFragments;
  gameState.nova.maxHealth = snapshot.maxHealth;
  gameState.nova.health = snapshot.maxHealth;
  // DECISION: Weapon kind + ammo roll back with the snapshot. This makes
  // death at least as punishing as a hit (which calls `resetWeaponOnHit`):
  // post-checkpoint pickups don't persist past a death, and the weapon
  // pickup itself is restored above so the player can re-grab it.
  gameState.projectileKind = snapshot.projectileKind;
  gameState.ammo = snapshot.ammo;

  resetProjectiles();
  clearParticles();
  resetHitEffectVfx();
  gameState.attackCooldown = 0;
  Logger.debug('PlayScene', 'Reset to checkpoint state');
}
